import React from "react";
import { useSelector } from "react-redux";

import makeStyles from "@material-ui/styles/makeStyles";

import {
  Create,
  Edit,
  List,
  Filter,
  SimpleForm,
  TextInput,
  BooleanInput,
  SelectInput,
  TextField,
  FunctionField,
  required,
  CreateButton,
  EditButton,
  DeleteButton,
  FilterButton
} from "jazasoft";

import Datagrid from "../../components/Datagrid";
import HistoryButton from "../../components/LibraryHistoryButton";

import useUnit from "../../hooks/useUnit";
import { inputOptions, isTrue, listFromCsv } from "../../utils/helpers";
import { useEntityPrivilege } from "../../hooks";
import { useSetting } from "../../hooks";
import SettingKeys from "../../utils/SettingKeys";
import { TableFields } from "../../utils/entityFields";
import getValidation from "../../utils/getValidation";

import Forbidden from "../../components/Forbidden";
import { Role } from "../../utils/types";

export const CreateTable = props => {
  const { unitChoices: unitList } = useUnit();
  const fields = useSetting(SettingKeys.ENTITY_FIELDS, props.resource);
  const validationRule = useSetting(SettingKeys.ENTITY_FIELD_VALIDATION, props.resource);
  const hasPrivilege = props.roles?.includes(Role.MASTER) || props.roles?.includes(Role.SUPER_USER);
  if (!hasPrivilege) {
    return <Forbidden history={props.history} />;
  }

  return (
    <Create {...props}>
      <SimpleForm redirect="home">
        {unitList?.length > 1 && <SelectInput validate={[required()]} source="unitIds" label="Units" choices={unitList} {...inputOptions(3)} />}
        {fields.includes(TableFields.NAME) && (
          <TextInput source="name" validate={getValidation(validationRule[TableFields.NAME])} {...inputOptions(3)} />
        )}
        {fields.includes(TableFields.DESC) && (
          <TextInput source="desc" validate={getValidation(validationRule[TableFields.DESC])} {...inputOptions(3)} />
        )}
        {fields.includes(TableFields.ENABLED) && <BooleanInput source="enabled" {...inputOptions(3)} />}
      </SimpleForm>
    </Create>
  );
};

export const EditTable = props => {
  const record = useSelector(
    state =>
      state.jazasoft.resources[props.resource] &&
      state.jazasoft.resources[props.resource].data &&
      state.jazasoft.resources[props.resource].data[props.id]
  );
  const { unitChoices } = useUnit(record);
  const fields = useSetting(SettingKeys.ENTITY_FIELDS, props.resource);
  const validationRule = useSetting(SettingKeys.ENTITY_FIELD_VALIDATION, props.resource);

  const hasPrivilege = props.roles?.includes(Role.MASTER) || props.roles?.includes(Role.SUPER_USER);
  if (!hasPrivilege) {
    return <Forbidden history={props.history} />;
  }

  return (
    <Edit {...props}>
      <SimpleForm redirect="home" form="record-form-edit">
        {unitChoices?.length > 1 && <SelectInput validate={[required()]} source="unitIds" label="Units" choices={unitChoices} {...inputOptions(3)} />}
        {fields.includes(TableFields.NAME) && (
          <TextInput
            source="name"
            validate={getValidation(validationRule[TableFields.NAME])}
            options={{ ...inputOptions(3), disabled: record && record.isReferenced }}
            {...inputOptions(3)}
          />
        )}
        {fields.includes(TableFields.DESC) && (
          <TextInput source="desc" validate={getValidation(validationRule[TableFields.DESC])} {...inputOptions(3)} />
        )}
        {fields.includes(TableFields.ENABLED) && <BooleanInput source="enabled" {...inputOptions(3)} />}
      </SimpleForm>
    </Edit>
  );
};

const useHomeStyle = makeStyles(theme => ({
  buttonEdit: {
    width: theme.spacing(14)
  },
  buttonDelete: {
    width: theme.spacing(16)
  },
  buttonHistory: {
    width: theme.spacing(1)
  }
}));

export const TableHome = props => {
  const classes = useHomeStyle();

  const { disableCreate, disableEdit, disableDelete } = useEntityPrivilege("table");
  const { allUnitList, unitChoices } = useUnit();
  const fields = useSetting(SettingKeys.ENTITY_FIELDS, props.resource);

  const hasPrivilege = props.roles?.includes(Role.MASTER) || props.roles?.includes(Role.SUPER_USER);

  return (
    <List
      actions={({ basePath }) => (
        <div>
          {hasPrivilege && !disableCreate && <CreateButton basePath={basePath} showLabel={false} />}
          {unitChoices.length > 1 && <FilterButton showLabel={false} />}
        </div>
      )}
      filters={
        <Filter>
          <SelectInput source="unitIds" i18nKey={props.i18nKey} choices={unitChoices} {...inputOptions(12)} />
        </Filter>
      }
      parse={({ unitIds, ...rest }) => ({ ...rest, unitIds: unitIds ? `*${unitIds}*` : undefined })}
      {...props}>
      <Datagrid classes={{ headerCell: classes.headerCell, rowCell: classes.rowCell }}>
        {fields.includes(TableFields.NAME) && <TextField source="name" />}
        {unitChoices.length > 1 && (
          <FunctionField
            source="unitIds"
            label="Units"
            render={record =>
              allUnitList
                .filter(u => listFromCsv(record?.unitIds).includes(u.unitId))
                .map(u => u.name)
                .join(", ")
            }
          />
        )}
        {fields.includes(TableFields.DESC) && <TextField source="desc" />}
        {fields.includes(TableFields.ENABLED) && <FunctionField source="enabled" render={record => (isTrue(record.enabled) ? "Yes" : "No")} />}
        <HistoryButton cellClassName={classes.buttonHistory} i18nKey={props.i18nKey} />
        {hasPrivilege && !disableEdit && <EditButton cellClassName={classes.buttonEdit} />}
        {hasPrivilege && !disableDelete && <DeleteButton cellClassName={classes.buttonDelete} />}
      </Datagrid>
    </List>
  );
};
